import React from 'react';
import { CustomIcon } from '../CustomIcon/CustomIcon';
import type { CustomIconName } from '../CustomIcon/CustomIcon';
import { Image } from '../Image/Image';
import './Badge.scss';

/**
 * Badge — shared pill component used as section-header eyebrows
 * (Medium / 18px) and inline card markers (Small / 14px).
 *
 * Consolidates the duplicated badge styling that previously lived in
 * SectionHeader, CardGrid, MegaMenu/FeaturedColumn, MegaMenu/LinkColumn,
 * MegaMenu/MobileAccordion, NsiCard, and FeaturedCards. All pill-style
 * badges in the library now render through this primitive; per-component
 * `__badge` classes survive only as layout-rhythm hooks (margin / wrap)
 * and are passed in via `className`.
 *
 * Multi-tone "info" badges (e.g. InfoBanner's did-you-know / pro-tip /
 * learn-the-lingo) and the uppercase category label in ContentGrid are
 * intentionally separate — they're a different visual system.
 */
export type BadgeSize = 'sm' | 'md';

/**
 * Background tone:
 * - `dark` — Squid Ink/900 with white text (used in cards / MegaMenu)
 * - `nsi`  — NSI green with white text (used as section header eyebrow)
 */
export type BadgeTone = 'dark' | 'nsi';

export interface BadgeProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Visual size. Default: `md` (18px). */
  size?: BadgeSize;
  /** Background tone. Default: `dark`. */
  tone?: BadgeTone;
  /** Optional icon rendered before the text (built-in icon set). */
  icon?: CustomIconName;
  /**
   * External icon image URL rendered before the text. Use this when the icon
   * comes from authored content (e.g. a CMS-supplied URL) rather than the
   * built-in `CustomIcon` set. Mutually exclusive with `icon` — `icon` wins
   * if both are provided.
   */
  iconUrl?: string;
  /** Badge label */
  children: React.ReactNode;
}

/**
 * Icon dimensions per badge size.
 * Mirror the existing `--poly-size-icon-sm` (16px) and `--poly-size-icon-md`
 * (20px) tokens. CustomIcon's `size` prop is numeric so we can't reference
 * the CSS variable directly here.
 */
const ICON_SIZE_SM = 16;
const ICON_SIZE_MD = 20;

export const Badge: React.FC<BadgeProps> = ({
  size = 'md',
  tone = 'dark',
  icon,
  iconUrl,
  children,
  className,
  ...rest
}) => {
  const hasIcon = Boolean(icon || iconUrl);

  if (process.env.NODE_ENV !== 'production' && icon && iconUrl) {
    // eslint-disable-next-line no-console
    console.warn(
      '[Badge] Both `icon` and `iconUrl` were provided; rendering `icon` and ignoring `iconUrl`. Use one or the other.',
    );
  }

  const classes = [
    'poly-badge',
    `poly-badge--${size}`,
    `poly-badge--${tone}`,
    hasIcon ? 'poly-badge--with-icon' : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={classes} {...rest}>
      {icon ? (
        <CustomIcon
          name={icon}
          size={size === 'sm' ? ICON_SIZE_SM : ICON_SIZE_MD}
          aria-hidden="true"
        />
      ) : iconUrl ? (
        <Image
          bare
          url={iconUrl}
          alt=""
          aria-hidden="true"
          className="poly-badge__icon-image"
        />
      ) : null}
      {/* The __text wrapper is intentional even though it carries no SCSS rule
          today: it gives the text a discrete DOM node so the badge always has
          a predictable two-flex-child layout (icon, text), and provides a
          stable hook for any future text-only styling without touching
          consumer DOM expectations. */}
      <span className="poly-badge__text">{children}</span>
    </span>
  );
};
